import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { gtmEvent } from '@dtvgo/gtm-event-report';

import CommunicationAdLink from '../../components/communicationAdLink/CommunicationAdLink';
import { useGtmContext } from '../../components/GtmContext';
import { formatBannerTitle, gtmCommunicationDimensions } from '../../components/GtmContext/dimensions/communication';
import { useImpressionOnVisible } from '../../hooks/useImpressionOnVisible';

import './RowAdLine.scss';

function RowAdLine({ row, horizontalOffset }) {
  const { gtmUserData } = useGtmContext();
  const [logoFailed, setLogoFailed] = useState(false);
  const {
    id,
    title,
    logoUrl,
    backgroundImageUrl,
    backgroundColor,
    linkHref,
  } = row;

  const rowAdRef = useImpressionOnVisible({
    resetKey: id,
    onImpression: () => {
      const { hash, eventType, dimensions } = gtmCommunicationDimensions.row_ads.page_view;
      gtmEvent({
        hash,
        eventType,
        dimensions,
        userData: { ...gtmUserData, v_banner_title: formatBannerTitle(title) },
        data: { tab: 'livelander' },
      });
    },
  });

  const handleBannerClick = useCallback(() => {
    const { hash, eventType, dimensions } = gtmCommunicationDimensions.row_ads.banner_click;
    gtmEvent({
      hash,
      eventType,
      dimensions,
      userData: { ...gtmUserData, v_banner_title: formatBannerTitle(title) },
      data: { tab: 'livelander' },
    });
  }, [gtmUserData, title]);

  const style = {};
  if (!backgroundImageUrl && backgroundColor) {
    style.backgroundColor = backgroundColor;
  }

  const showLogo = logoUrl && !logoFailed;
  const hasText = Boolean(title);

  const ariaLabel = title || 'Advertisement';

  const inner = (
    <>
      {backgroundImageUrl && (
        <div
          className="dtv-epg-row-ad-line__bg-image"
          style={{ backgroundImage: `url(${backgroundImageUrl})` }}
          aria-hidden="true"
        />
      )}
      <div className="dtv-epg-row-ad-line__inner">
        <div className="dtv-epg-row-ad-line__content">
          {showLogo && (
            <div className="dtv-epg-row-ad-line__logo-wrap">
              <img
                src={logoUrl}
                alt="logo"
                className="dtv-epg-row-ad-line__logo"
                onError={() => setLogoFailed(true)}
              />
            </div>
          )}
          {hasText && (
            <div className="dtv-epg-row-ad-line__text">
              {title && <p className="dtv-epg-row-ad-line__title">{title}</p>}
            </div>
          )}
          {!hasText && !showLogo && (
            <div className="dtv-epg-row-ad-line__text" aria-hidden="true" />
          )}
        </div>
      </div>
    </>
  );

  const rootClassName = classNames('dtv-epg-row-ad-line', 'dtv-epg-row-ad-line--sticky-horizontal', {
    'dtv-epg-row-ad-line--has-bg-image': Boolean(backgroundImageUrl),
    'dtv-epg-row-ad-line--is-link': Boolean(linkHref),
  });

  style.transform = `translateX(${-horizontalOffset}px)`;

  const rootStyle = style;

  if (linkHref) {
    return (
      <CommunicationAdLink
        ref={rowAdRef}
        href={linkHref}
        className={rootClassName}
        style={rootStyle}
        ariaLabel={ariaLabel}
        onBeforeNavigate={handleBannerClick}
      >
        {inner}
      </CommunicationAdLink>
    );
  }

  return (
    <div
      ref={rowAdRef}
      className={rootClassName}
      style={rootStyle}
      role="region"
      aria-label={ariaLabel}
    >
      {inner}
    </div>
  );
}

RowAdLine.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    logoUrl: PropTypes.string,
    backgroundImageUrl: PropTypes.string,
    backgroundColor: PropTypes.string,
    linkHref: PropTypes.string,
  }).isRequired,
  horizontalOffset: PropTypes.number,
};

RowAdLine.defaultProps = {
  horizontalOffset: 0,
};

export default RowAdLine;
